import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Shield } from "lucide-react";
import { PERMISSIONS_BY_CATEGORY } from "@/types/roles";

interface PermissionsSelectorProps {
  selectedPermissions: string[];
  onPermissionsChange: (permissions: string[]) => void;
  error?: string;
}

export function PermissionsSelector({
  selectedPermissions,
  onPermissionsChange,
  error,
}: PermissionsSelectorProps) {
  const permissionsByCategory = PERMISSIONS_BY_CATEGORY;

  const togglePermission = (permissionId: string) => {
    if (selectedPermissions.includes(permissionId)) {
      onPermissionsChange(
        selectedPermissions.filter((p) => p !== permissionId),
      );
    } else {
      onPermissionsChange([...selectedPermissions, permissionId]);
    }
  };

  const toggleCategory = (category: string) => {
    const categoryPermissions =
      permissionsByCategory[category]?.map((p) => p.id) || [];

    const allSelected = categoryPermissions.every((p) =>
      selectedPermissions.includes(p),
    );

    if (allSelected) {
      onPermissionsChange(
        selectedPermissions.filter((p) => !categoryPermissions.includes(p)),
      );
    } else {
      const newPermissions = [...selectedPermissions];
      categoryPermissions.forEach((p) => {
        if (!newPermissions.includes(p)) {
          newPermissions.push(p);
        }
      });
      onPermissionsChange(newPermissions);
    }
  };

  const isCategorySelected = (category: string) => {
    const categoryPermissions =
      permissionsByCategory[category]?.map((p) => p.id) || [];
    return (
      categoryPermissions.length > 0 &&
      categoryPermissions.every((p) => selectedPermissions.includes(p))
    );
  };

  const isCategoryPartiallySelected = (category: string) => {
    const categoryPermissions =
      permissionsByCategory[category]?.map((p) => p.id) || [];
    const selectedCount = categoryPermissions.filter((p) =>
      selectedPermissions.includes(p),
    ).length;

    return selectedCount > 0 && selectedCount < categoryPermissions.length;
  };

  const totalPermissions = Object.values(permissionsByCategory).flat().length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold text-slate-700">Configuración de Permisos *</Label>
        <Badge variant="outline" className="text-[10px] font-normal">
          {selectedPermissions.length} / {totalPermissions} Seleccionados
        </Badge>
      </div>

      {error && (
        <p className="text-red-500 text-sm mb-3 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}

      <div className="space-y-4 border rounded-lg p-4 max-h-[450px] overflow-y-auto bg-slate-50/50">
        {Object.entries(permissionsByCategory).map(
          ([category, permissions]) => {
            const selectedInCategory = permissions.filter((p) => selectedPermissions.includes(p.id)).length;
            
            return (
              <div key={category} className="space-y-2">
                {/* CATEGORY */}
                <div className="flex items-center justify-between border-b pb-1 mb-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={`category-${category}`}
                      checked={isCategorySelected(category)}
                      onCheckedChange={() => toggleCategory(category)}
                      className={
                        isCategoryPartiallySelected(category)
                          ? "data-[state=checked]:bg-blue-400"
                          : ""
                      }
                    />
                    <Label
                      htmlFor={`category-${category}`}
                      className="font-semibold text-sm text-slate-700 cursor-pointer"
                    >
                      {category}
                    </Label>
                  </div>
                  <span className="text-[10px] text-slate-500 font-medium bg-slate-100 px-1.5 py-0.5 rounded">
                    {selectedInCategory} / {permissions.length}
                  </span>
                </div>

                {/* PERMISSIONS */}
                <div className="grid grid-cols-2 gap-2 pl-6">
                  {permissions.map((permission) => (
                    <div
                      key={permission.id}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={permission.id}
                        checked={selectedPermissions.includes(permission.id)}
                        onCheckedChange={() => togglePermission(permission.id)}
                      />
                      <Label
                        htmlFor={permission.id}
                        className="text-sm cursor-pointer text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        {permission.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
        )}
      </div>

      <div className="flex items-center gap-2 text-[10px] text-slate-500">
        <Shield className="h-3 w-3" />
        <span>Los cambios en los permisos se aplican inmediatamente a todos los usuarios con este rol.</span>
      </div>
    </div>
  );
}
